import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { FILESTACK_API_KEY, IS_PRODUCTION } from 'utils/globals';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import FSPicker from 'components/shared/FSPicker';

import photoModule from 'domains/photos';
import session from 'domains/session';
import ui from 'domains/ui';

import CollectionPicker from './CollectionPicker';

import { PickerFileMetadata, PickerResponse } from 'filestack-js';

const options = {
  maxFiles: 50,
  maxSize: 1024 * 1024 * 1024, // 1GB
  accept: ['image/jpeg', 'image/png', 'image/tiff'],
  fromSources: ['local_file_system', 'facebook', 'instagram', 'googledrive', 'dropbox'],
  concurrency: 3, // <number> - Max number of concurrent parts uploading (chunks of files, not whole files).
  disableTransformer: true,
  storeTo: {
    location: 's3',
    path: IS_PRODUCTION ? '/wos-production/' : '/wos-stage/',
  },
};

function UploadPhotos() {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const showPicker = useAppSelector(ui.selectors.showUploadPhotosDialog);

  const collections = useAppSelector(photoModule.selectors.allNamedCollections);
  const uncollected = useAppSelector(photoModule.selectors.uncollectedCollection);
  const pendingUploadCollection = useAppSelector(photoModule.selectors.newUploadsCollection);

  const [selectedPickerCollection, setSelectedPickerCollection] = useState<string>('');

  // Using a temporary state, unchanging callback, & useEffect prevents the picker from
  // rendering (and re-initializing) every time the selected collection changes.
  const [uploads, setUploads] = useState<PickerFileMetadata[]>();
  const handleUploadDone = useCallback((res: PickerResponse) => setUploads(res.filesUploaded), []);

  function handleShowPicker(show: boolean) {
    dispatch(ui.actions.setShowUploadPhotosDialog(show));
  }

  useEffect(() => {
    if (uploads) {
      dispatch(
        photoModule.actions.uploadPhotos.pending({
          uploads,
          collection: selectedPickerCollection,
        })
      );
    }
  }, [uploads, selectedPickerCollection]);

  return (
    <React.Fragment>
      <div className="upload_photos_trigger_container">
        <Button
          id="upload-photos-trigger"
          type="cornered"
          className="upload_photos_trigger"
          onClick={() => handleShowPicker(true)}
        >
          <FontAwesomeIcon icon="cloud-upload-alt" size="lg" />
          <span>
            Upload
            <br />
            Photos
          </span>
        </Button>
      </div>

      {showPicker &&
        renderModal(
          <Modal
            onClose={() => handleShowPicker(false)}
            lastFocusedElementId="upload-photos-trigger"
            labelledBy="upload-photos"
            title="Upload Photos"
            modalClass={['upload-photo-picker']}
          >
            <FSPicker
              apikey={FILESTACK_API_KEY}
              pickerOptions={options}
              onUploadDone={handleUploadDone}
            />
            {isLoggedIn && (
              <CollectionPicker
                onChange={setSelectedPickerCollection}
                collections={collections}
                uncollected={uncollected}
                pending={pendingUploadCollection}
                selected={selectedPickerCollection}
              />
            )}
          </Modal>
        )}
    </React.Fragment>
  );
}

export default UploadPhotos;
